//------- import libraries
import { connect } from "react-redux";
import { Col, Modal, Row } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";

//------- import custom reuseable components
import ItemImage from "../.././../../assets/images/productimage.png";
import Button from "../../../../shared/components/Buttons/Button";

//------- import constants
import {
  buttonNameConstants,
  buttonTypeConstants,
  inventoryConstants,
} from "../../../../constants";
import SelectDropDown from "../../../../shared/components/selectDropdown/SelectDropdown";
import {
  getAddInventoryPricePayloadObject,
  getAddInventoryRootPayloadPartialObject,
  getShortestSku,
  handleKeyDown,
  isSpinnerEnabled,
} from "../../../../shared/utility";
import { inventoryActions } from "../../../../redux/actions";
import ProductImageCard from "../../../../shared/components/productDetailCard/ProductImageCard";

const TransferInventoryModal = ({
  stores,
  spinnerArray,
  selectedRows,
  currentStore,
  singlePrintArray,
  isEditInventory = false,
  isTransferInventoryModal,
  toggleTransferInventoryModal,
  handleTransferCompanyInventory,
  stateData = {},
}) => {
  //------- usestate
  const [toStore, setToStore] = useState(false);
  const [storeOptions, setStoreOptions] = useState([]);

  //--------data to transfer
  const [data, setData] = useState(() => {
    const products =
      selectedRows?.length > 0
        ? selectedRows
        : singlePrintArray?.length > 0
        ? singlePrintArray
        : [];
    return products.map((item) => ({
      ...item,
      transferQuantity: item.price.quantity < 1 ? 0 : 1,
    }));
  });

  //------- Handle Cancel Button Click
  const handleCancelButtonClick = () => {
    toggleTransferInventoryModal();
  };

  //------- Handle Quantity Change
  const handleQuantityChange = (sku, value) => {
    const shortestSku = getShortestSku(sku);
    const updatedData = data.map((item) => {
      if (getShortestSku(sku) === shortestSku && item.price.quantity >= value) {
        return {
          ...item,
          transferQuantity: value ? parseInt(value) : 0,
        };
      }
      return item;
    });
    setData(updatedData);
  };

  //------- Column Headers for DataTable
  //-------column headers for the print modal table
  const columnHeaders = [
    {
      id: "image",
      name: "Image",
      selector: (row) => row.image,
      cell: (row) => {
        return (
          <div className="product-image product-image-transfer">
            <ProductImageCard src={row.imgUrl || ItemImage} />
          </div>
        );
      },
      sortable: true,
      sortDirection: null,
      width: "5%",
    },
    {
      id: "productName",
      name: "Product Name",
      selector: (row) => row.product_name,
      cell: (row) => {
        return (
          <span className="devices-cell-max-size">{row.product_name}</span>
        );
      },
      sortable: true,
      sortDirection: null,
      width: "15%",
    },
    {
      id: "type",
      name: "Type",
      selector: (row) => row.productType,
      cell: (row) => {
        return <span className="devices-cell-max-size">{row.productType}</span>;
      },
      sortable: true,
      sortDirection: null,
      width: "10%",
    },
    {
      id: "category",
      name: "Category",
      selector: (row) => row.category_name,
      cell: (row) => {
        return (
          <span className="devices-cell-max-size">{row.category_name}</span>
        );
      },
      sortable: true,
      sortDirection: null,
      width: "15%",
    },
    {
      id: "condition",
      name: "Condition",
      selector: (row) => row.price.type,
      cell: (row) => {
        return <span className="devices-cell-max-size">{row.price.type}</span>;
      },
      sortable: true,
      sortDirection: null,
      width: "10%",
    },
    {
      id: "tags",
      name: "Tags",
      selector: (row) => row.tags,
      cell: (row) =>
        row.tags?.filter((tag) => tag !== inventoryConstants.CUSTOM_ITEM_TAG)
          ?.length || 0,
      sortable: true,
      sortDirection: null,
      width: "10%",
    },
    {
      id: "unitSellPrice",
      name: "Unit Sell Price",
      selector: (row) => row.price.unit_sell_price,
      cell: (row) => {
        return (
          <span className="devices-cell-max-size">
            {row.price.unit_sell_price}
          </span>
        );
      },
      sortable: true,
      sortDirection: null,
      width: "10%",
    },
    {
      id: "inStockQuantity",
      name: "In Stock Quantity",
      selector: (row) => row.price.quantity,
      cell: (row) => {
        return (
          <span className="devices-cell-max-size">{row.price.quantity}</span>
        );
      },
      sortable: true,
      sortDirection: null,
      width: "10%",
    },
    {
      id: "quantity",
      name: (
        <span className="d-flex w-100 justify-content-center">Quantity</span>
      ),
      selector: (row) => row.transferQuantity,
      cell: (row) => {
        return (
          <input
            min={1}
            type="number"
            max={Number(row.price.quantity)}
            value={row.transferQuantity}
            onKeyDown={handleKeyDown}
            className="print-input-field"
            onChange={(e) => handleQuantityChange(row.sku, e.target.value)}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "15%",
    },
  ];

  useEffect(() => {
    // Update store options when stores change, only include stores with same companyId as currentStore
    setStoreOptions(
      stores
        ?.filter(
          (store) =>
            store.id !== currentStore?.id &&
            store.companyId === currentStore?.companyId
        )
        .map((store) => ({
          value: store.id,
          label: store.storeName,
        }))
    );
  }, [stores, currentStore]);

  //-------handle transfer button click
  const handleTransfer = () => {
    if (toStore && data?.length > 0) {
      const dataToSend = {
        from: {
          store: {
            id: currentStore?.id,
            name: currentStore?.storeName,
          },
          inventory: data.map((item) => {
            let updatedItem = {
              ...item,
              ...getAddInventoryRootPayloadPartialObject({
                ...item,
                genre: item.subcategory,
              }),
              price: {
                ...item.price,
                ...getAddInventoryPricePayloadObject({
                  ...item.price,
                  costOfGoods: item.price.unit_purchase_price,
                  inStockPrice: item.price.unit_sell_price,
                  quantity: item.transferQuantity,
                }),
              },
            };
            delete updatedItem.storeIdAsPartitionKey;
            return updatedItem;
          }),
        },
        to: {
          store: { id: toStore?.value, name: toStore?.label },
        },
      };
      handleTransferCompanyInventory(
        dataToSend,
        toggleTransferInventoryModal,
        isEditInventory,
        stateData
      );
    }
  };

  const hasZeroTransferQuantity = () => {
    return data.some((item) => item.transferQuantity === 0);
  };

  return (
    <Modal
      centered
      size="xl"
      animation={true}
      backdrop="static"
      show={isTransferInventoryModal}
      onHide={toggleTransferInventoryModal}
      className="add-inventory-modal"
    >
      <Modal.Header closeButton className="add-inventory-modal-header">
        <Modal.Title>
          <span className="add-inventory-modal-header-name">
            Transfer Inventory
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="add-inventory-modal-body ">
        <Row className="m-0">
          <Col md={12} className="ps-0 mb-3">
            <h5 className="label-modal-text-heading mb-0">
              From : {currentStore?.storeName}
            </h5>
          </Col>
          <Col md={12} className="ps-0 d-flex align-items-center gap-2 mb-3">
            <h5 className="label-modal-text-heading mb-0">To :</h5>
            <SelectDropDown
              menuPortalTarget={""}
              menuPlacement={"bottom"}
              placeHolder="Select Store..."
              options={storeOptions}
              value={toStore}
              handleChange={(e) => {
                setToStore(e);
              }}
            />
          </Col>
          <Col md={12}>
            <DataTable data={data} columns={columnHeaders} fixedHeader />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Row>
          <Col md={6}></Col>
          <Col
            md={6}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            <Button
              label={buttonNameConstants.CANCEL}
              handleClick={() => handleCancelButtonClick()}
              buttonType={buttonTypeConstants.GHOST_BUTTON}
              isDisabled={isSpinnerEnabled(
                spinnerArray,
                inventoryConstants.TRANSFER_INVENTORY
              )}
            />
            <Button
              label={buttonNameConstants.TRANSFER}
              handleClick={handleTransfer}
              isDisabled={
                !toStore ||
                hasZeroTransferQuantity() ||
                data?.length < 0 ||
                isSpinnerEnabled(
                  spinnerArray,
                  inventoryConstants.TRANSFER_INVENTORY
                )
              }
            />
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  stores: state.store.stores,
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  handleTransferCompanyInventory: (
    data,
    handleHideModal,
    isEditInventory,
    stateData
  ) =>
    dispatch(
      inventoryActions.handleTransferCompanyInventory(
        data,
        handleHideModal,
        isEditInventory,
        stateData
      )
    ),
});

//-------Export Transfer Inventory Modal Component
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TransferInventoryModal);
